import React, { useEffect, useState, useRef } from 'react'
import { Button, Card, Row, Col } from 'react-bootstrap'
import text from '../../../_constants/text'
import { Practice } from '../../../reducers/AccountReducer/AccountReducer'
import AddPracticeModal from './AddPracticeModal'
import RemovePracticeModal from './RemovePracticeModal'
import { connect } from 'react-redux'
import AlertBox from '../../../components/molecules/AlertBox'
import { removePracticeReset } from '../../../actions/RemovePractice/removePracticeActions'
import { resetForm } from '../../../actions/AddPractice/addPracticeActions'

interface PracticeCardProps {
  practices: Practice[]
  addPracticeSuccess: boolean
  removePracticeSuccess: boolean
  resetForm: () => void
  removePracticeReset: () => void
}

const PracticesCard: React.FC<PracticeCardProps> = ({
  practices,
  addPracticeSuccess,
  removePracticeSuccess,
  resetForm,
  removePracticeReset,
}) => {
  const [showAddPracticeModal, setShowAddPracticeModal] = useState(false)
  const [showRemovePracticeModal, setShowRemovePracticeModal] = useState(false)
  const [showAddSuccess, setShowAddSuccess] = useState(false)
  const [showRemoveSuccess, setShowRemoveSuccess] = useState(false)
  const successTimeout = useRef<NodeJS.Timeout | null>(null)
  const ALERT_TIMEOUT = 5000

  const handleShowAddPracticeModal = () => {
    setShowAddPracticeModal((prev) => {
      const willShow = !prev
      if (willShow) resetForm() // Reset only when opening
      return willShow
    })
  }

  const handleShowRemovePracticeModal = () => {
    setShowRemovePracticeModal((prev) => {
      const willShow = !prev
      if (willShow) removePracticeReset() // Reset only when opening
      return willShow
    })
  }

  useEffect(() => {
    if (addPracticeSuccess) {
      setShowAddPracticeModal(false)
      setShowAddSuccess(true)
      if (successTimeout.current) clearTimeout(successTimeout.current)
      successTimeout.current = setTimeout(() => {
        setShowAddSuccess(false)
        resetForm() // Reset Redux flag after alert hides
      }, ALERT_TIMEOUT)
    }
    return () => {
      if (successTimeout.current) clearTimeout(successTimeout.current)
    }
  }, [addPracticeSuccess])

  useEffect(() => {
    if (removePracticeSuccess) {
      setShowRemovePracticeModal(false)
      setShowRemoveSuccess(true)
      if (successTimeout.current) clearTimeout(successTimeout.current)
      successTimeout.current = setTimeout(() => {
        setShowRemoveSuccess(false)
        removePracticeReset() // Reset Redux flag after alert hides
      }, ALERT_TIMEOUT)
    }
    return () => {
      if (successTimeout.current) clearTimeout(successTimeout.current)
    }
  }, [removePracticeSuccess])

  return (
    <Card as="section" className="mb-3">
      <Card.Body>
        <Card.Title as="h2" className="mb-4">
          {text.PRACTICES}
        </Card.Title>
        {showAddSuccess && <AlertBox type="success" message={text.ADD_PRACTICE_SUCCESS} />}
        {showRemoveSuccess && <AlertBox type="success" message={text.REMOVE_PRACTICE_SUCCESS} />}
        {practices.map((practice) => (
          <div key={practice.uri}>
            <Row className="my-3 d-flex flex-row px-2 px-lg-0">
              <Col md={9}>
                <p className="mb-0">{practice.practiceName}</p>
              </Col>
            </Row>
          </div>
        ))}
        <AddPracticeModal show={showAddPracticeModal} handleShow={handleShowAddPracticeModal} />
        <div className="mt-3">
          <Row>
            <Col className="d-flex flex-row gap-2">
              <Button onClick={handleShowAddPracticeModal}>{text.ADD_ANOTHER_PRACTICE}</Button>
              {practices.length > 0 && (
                <>
                  <RemovePracticeModal show={showRemovePracticeModal} handleShow={handleShowRemovePracticeModal} />
                  <Button onClick={handleShowRemovePracticeModal}>{text.REMOVE_EXISTING_PRACTICE}</Button>
                </>
              )}
            </Col>
          </Row>
        </div>
      </Card.Body>
    </Card>
  )
}

const mapStateToProps = (state) => {
  const { success: addPracticeSuccess } = state.addPractice.submit
  const { success: removePracticeSuccess } = state.removePractice.submit
  return { addPracticeSuccess, removePracticeSuccess }
}

const mapDispatchToProps = (dispatch) => {
  return {
    resetForm: () => dispatch(resetForm()),
    removePracticeReset: () => dispatch(removePracticeReset()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PracticesCard)
